import React, { useEffect } from "react";
import styled from "styled-components";
import * as R from "ramda";
import * as Yup from "yup";
import { Modal } from "antd";
import { Formik, Form } from "formik";
import { Input } from "antd";
import { withTranslation } from "react-i18next";
import { compose } from "recompose";
import { connect } from "react-redux";
import { isLoading } from "@worcket/common/hocs";

import * as gtag from "../../lib/gtag";
import { Field } from "../../components/controls/FormikForm";
import { addCompany, ADD_COMPANY } from "../../store/actions/company";
import ProfileFormInputs from "../Company/ProfileFormInputs";
import Button from "../../components/Button";
import { ACCEPT_LANGUAGE } from "../../locales/acceptedLanguages";

const AdminInvitation = styled.div`
  .description {
    margin-bottom: 2.5em;
  }

  .optional {
    font-size: 0.75em;
    margin-left: 8px;
    color: ${({ theme }) => theme["@grey-medium"]};
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding-right: 40px;

  .step {
    color: ${({ theme }) => theme["@primary"]};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CompanyModal = ({
  t,
  history,
  addCompany,
  loading,
  companiesNames,
  userEmail,
  match,
  headquarter
}) => {
  const {
    params: { type }
  } = match;
  const [showInviteScreen, setShowInviteScreen] = React.useState(false);
  const [prefix, setPrefix] = React.useState("https://");

  useEffect(() => {
    gtag.event({
      category: "Headquarter",
      action: "Headquarter.Company.NewCompany",
      label: headquarter.id
    });
  }, [headquarter.id]);

  const newCompanyValidation = Yup.object().shape({
    name: Yup.string()
      .ensure()
      .required(t("form:emptyFieldValidation"))
      .test(
        "does-not-have-underscores",
        t("form:noUnderscoresValidation"),
        name => !name.includes("_")
      )
      .test(
        "is-unique",
        t("company:profile.alreadyUsedName"),
        name => !R.includes(name, companiesNames)
      ),
    nameAdmin: Yup.string().test(
      "required-if-email",
      t("form:emptyFieldValidation"),
      function(value) {
        const { emailAdmin } = this.parent;
        if (emailAdmin) return !!value;
        return true;
      }
    ),
    emailAdmin: Yup.string()
      .email(t("form:validEmail"))
      .test("required-if-name", t("form:emptyFieldValidation"), function(
        value
      ) {
        const { nameAdmin } = this.parent;
        if (nameAdmin) return !!value;
        return true;
      })
      .notOneOf([userEmail], t("companies:adminStep.sameUserEmail")),
    country: Yup.string().required(t("form:emptyFieldValidation")),
    address: Yup.string().required(t("form:emptyFieldValidation")),
    loginUrl:
      type === "university"
        ? Yup.string().required(t("form:emptyFieldValidation"))
        : Yup.string(),
    website: Yup.string().matches(
      /^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      t("form:urlValidation")
    )
  });

  return (
    <Formik
      initialValues={{
        name: "",
        country: headquarter.country,
        language: headquarter.language,
        address: "",
        type,
        allowMultiposting: headquarter.allowMultiposting,
        privateJobBoard: type === "university",
        ...(type === "university" ? { loginUrl: "" } : {})
      }}
      onSubmit={async (values, { setFieldError }) => {
        gtag.event({
          category: "Headquarter",
          action: showInviteScreen
            ? "Headquarter.Company.NewCompany.confirm"
            : "Headquarter.Company.NewCompany.next",
          label: headquarter.id
        });

        const newCountry = values.country || headquarter.country;

        values.language =
          ACCEPT_LANGUAGE[newCountry] ||
          headquarter.language ||
          ACCEPT_LANGUAGE.DEFAULT;

        if (showInviteScreen) {
          addCompany(
            R.filter(R.identity, {
              ...values,
              name: values.name.trim(),
              loginUrl:
                values.type === "university"
                  ? `${prefix}${values.loginUrl}`
                  : ""
            }),
            {
              closeModal: () => {
                history.push("/compañias");
              },
              onExistingUserError: () =>
                setFieldError(
                  "emailAdmin",
                  t("companies:adminStep.existingUserError")
                )
            }
          );
        } else {
          setShowInviteScreen(true);
        }
      }}
      validationSchema={newCompanyValidation}
    >
      {({ handleSubmit, values, setFieldValue, errors, touched }) => (
        <Modal
          title={
            <ModalHeader>
              <span>
                {type === "university"
                  ? t("companies:addUniversity")
                  : t("companies:companyDetails:title")}
              </span>
              <span className="step">
                {t("companies:adminStep.stepOf", {
                  currentStep: showInviteScreen ? "2" : "1",
                  maxSteps: "2"
                })}
              </span>
            </ModalHeader>
          }
          visible
          width={700}
          onCancel={() => history.push("/compañias")}
          footer={
            <ModalFooter>
              {showInviteScreen ? (
                <Button
                  type="quaternary"
                  onClick={() => setShowInviteScreen(false)}
                >
                  {t("global:back")}
                </Button>
              ) : (
                <span />
              )}
              <Button
                data-test={
                  showInviteScreen ? "create-company-btn" : "next-step-btn"
                }
                disabled={R.not(R.isEmpty(errors))}
                onClick={handleSubmit}
                type="primary"
                loading={loading}
              >
                {showInviteScreen
                  ? type !== "university"
                    ? t("companies:createCompany")
                    : "Crear Institución académica"
                  : t("global:continue")}
              </Button>
            </ModalFooter>
          }
          destroyOnClose
          wrapProps={{
            "data-test": "company-modal"
          }}
        >
          <Form layout="vertical" onSubmit={handleSubmit}>
            {showInviteScreen ? (
              <AdminInvitation>
                <h2>
                  {t("companies:adminStep.title")}
                  <span className="optional">
                    ({t("companies:adminStep.optional")})
                  </span>
                </h2>
                <p className="description">
                  {t("companies:adminStep.description")}
                </p>
                <Field
                  name="nameAdmin"
                  label={t("companies:adminStep.nameAdmin")}
                >
                  <Input />
                </Field>
                <Field
                  name="emailAdmin"
                  label={t("companies:adminStep.emailAdmin")}
                  {...(errors.emailAdmin && touched.emailAdmin
                    ? {}
                    : {
                        help: t("companies:adminStep.notUserWarning")
                      })}
                >
                  <Input />
                </Field>
              </AdminInvitation>
            ) : (
              <ProfileFormInputs
                type={type}
                values={values}
                isNewCompany={true}
                setFieldValue={setFieldValue}
                setPrefix={setPrefix}
              />
            )}
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

const enhancer = compose(
  withTranslation(["global", "form", "company", "companies"]),
  isLoading(ADD_COMPANY),
  connect(
    state => ({
      headquarter: R.path(["headquarter"], state),
      companiesNames: R.pipe(
        R.propOr([], "companies"),
        R.pluck("name")
      )(state),
      userEmail: R.path(["user", "email"], state)
    }),
    { addCompany }
  )
);

export default enhancer(CompanyModal);
